import React, { useState,useEffect, useCallback, useMemo, memo } from 'react';
import moment from 'moment';
import api from '../../../api';

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, eventName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
        {/* Icon */}
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="pt-8 px-6 pb-6">
          <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
            Delete Event
          </h3>
          
          <p className="text-gray-600 text-center mb-6">
            Are you sure you want to delete
            <span className="block font-semibold text-gray-900 mt-1">
              "{eventName || 'this event'}"
            </span>
            ? This action cannot be undone.
          </p>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors shadow-lg shadow-red-100"
            >
              Delete Event
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast Notification Component
const Toast = ({ message, type = 'success', onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  if (!isVisible) return null;

  const bgColor = type === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500';
  const textColor = type === 'success' ? 'text-green-800' : 'text-red-800';
  const iconColor = type === 'success' ? 'text-green-500' : 'text-red-500';

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-md w-full mx-4 border-l-4 ${bgColor} bg-white rounded-xl shadow-xl p-4 transform transition-all duration-300 animate-slide-in`}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 ${iconColor}`}>
          {type === 'success' ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>
        <div className="flex-1">
          <p className={`font-medium ${textColor}`}>{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Memoized event item component with enhanced design
const EventItem = memo(({ event, onClick, onDelete }) => {
  const getEventTypeStyles = useMemo(() => {
    const styles = {
      academic: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
        dot: 'bg-blue-500',
        hover: 'hover:bg-blue-100'
      },
      exam: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-700',
        dot: 'bg-yellow-500',
        hover: 'hover:bg-yellow-100'
      },
      holiday: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        dot: 'bg-red-500',
        hover: 'hover:bg-red-100'
      },
      administrative: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-700',
        dot: 'bg-green-500',
        hover: 'hover:bg-green-100'
      }
    };
    return styles[event.eventType] || styles.academic;
  }, [event.eventType]);

  return (
    <div className="relative group/event">
      <div
        className={`w-full text-left px-3 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${getEventTypeStyles.bg} ${getEventTypeStyles.border} ${getEventTypeStyles.text} ${getEventTypeStyles.hover}`}
        title={event.eventName}
        onClick={(e) => {
          e.stopPropagation();
          onClick(event);
        }}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-2 h-2 rounded-full ${getEventTypeStyles.dot} flex-shrink-0`}></div>
          <span className="truncate flex-1 font-medium">{event.eventName}</span>
          {event.eventTime && (
            <span className="text-[10px] opacity-75 flex-shrink-0">
              {moment(event.eventTime).format('HH:mm')}
            </span>
          )}
        </div>
      </div>
      
      {/* Delete button - appears on hover */}
      <button
        className="absolute -right-1 -top-1 opacity-0 group-hover/event:opacity-100 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center transition-all duration-200 shadow-md hover:scale-110"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(event);
        }}
        title="Delete event"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
});

EventItem.displayName = 'EventItem';

// Memoized day cell component with enhanced design
const DayCell = memo(({ 
  date, 
  isSunday, 
  isUnblocked, 
  events, 
  isToday, 
  onDateClick, 
  onEventClick, 
  onDeleteEvent,
  onUnblockSunday, 
  onBlockSunday 
}) => {
  const dateStr = date.format('YYYY-MM-DD');
  const dayNumber = date.format('D');
  
  const handleDateClick = useCallback(() => {
    if (!(isSunday && !isUnblocked)) {
      onDateClick(dateStr, isSunday, isUnblocked);
    }
  }, [dateStr, isSunday, isUnblocked, onDateClick]);

  const handleUnblock = useCallback((e) => {
    e.stopPropagation();
    if (typeof onUnblockSunday === 'function') {
      onUnblockSunday(dateStr);
    }
  }, [dateStr, onUnblockSunday]);

  const handleBlock = useCallback((e) => {
    e.stopPropagation();
    if (typeof onBlockSunday === 'function') {
      onBlockSunday(dateStr);
    }
  }, [dateStr, onBlockSunday]);

  return (
    <div className={`bg-white min-h-[130px] p-2 relative group transition-all duration-200 ${
      isToday ? 'ring-2 ring-blue-500 ring-inset shadow-sm' : ''
    }`}>
      <button
        disabled={isSunday && !isUnblocked}
        onClick={handleDateClick}
        className={`w-full h-full text-left p-2 rounded-xl transition-all duration-200 ${
          isSunday && !isUnblocked
            ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
            : isSunday && isUnblocked
            ? 'bg-green-50 hover:bg-green-100 text-green-800 cursor-pointer'
            : 'hover:bg-blue-50 cursor-pointer'
        } ${isToday ? 'bg-blue-50' : ''}`}
      >
        <div className="flex justify-between items-start mb-2">
          <span className={`text-lg font-bold ${
            isSunday && !isUnblocked
              ? 'text-gray-400'
              : isSunday && isUnblocked
              ? 'text-green-700'
              : isToday
              ? 'text-blue-600'
              : 'text-gray-800'
          }`}>
            {dayNumber}
          </span>
          
          <div className="flex gap-1">
            {isToday && (
              <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-full font-medium">
                Today
              </span>
            )}
            
            {isSunday && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                isUnblocked
                  ? 'bg-green-200 text-green-700'
                  : 'bg-red-200 text-red-700'
              }`}>
                {isUnblocked ? '✓ Unblocked' : 'Sunday'}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-1">
          {events
            .slice()
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
            .slice(0, 3)
            .map((ev) => (
              <EventItem 
                key={ev.id || ev._id} 
                event={ev} 
                onClick={onEventClick}
                onDelete={onDeleteEvent}
              />
            ))}
          
          {events.length === 0 && !(isSunday && !isUnblocked) && (
            <div className="text-gray-400 text-xs italic text-center py-1.5 bg-gray-50 rounded-lg border border-dashed border-gray-200">
              Click to add event
            </div>
          )}
          
          {events.length > 3 && (
            <div className="text-xs text-gray-500 text-center bg-gray-100 rounded-lg py-1 font-medium">
              +{events.length - 3} more
            </div>
          )}
        </div>
      </button>

      {isSunday && (
        !isUnblocked
          ? typeof onUnblockSunday === 'function' && (
              <button
                className="absolute bottom-2 right-2 px-2.5 py-1 text-xs rounded-lg bg-green-500 text-white shadow-md hover:bg-green-600 transition-all duration-200 hover:scale-105 font-medium"
                onClick={handleUnblock}
              >
                Unblock
              </button>
            )
          : typeof onBlockSunday === 'function' && (
              <button
                className="absolute bottom-2 right-2 px-2.5 py-1 text-xs rounded-lg bg-red-500 text-white shadow-md hover:bg-red-600 transition-all duration-200 hover:scale-105 font-medium"
                onClick={handleBlock}
              >
                Block
              </button>
            )
      )}
    </div>
  );
});

DayCell.displayName = 'DayCell';

const CalendarGrid = ({ 
  calendarConfig,
  selectedSemesterType,
  calendarEvents,
  unblockedSundays,
  onDateClick,
  onEventClick,
  onDeleteEvent,
  onUnblockSunday,
  onBlockSunday,
  onEditEvent,
  eventListRef,
  onViewAllEvents,
  tenantId
}) => {
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [toast, setToast] = useState(null);

  // Handle delete with confirmation modal
  const handleDeleteClick = useCallback((event) => {
    setEventToDelete(event);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (eventToDelete && onDeleteEvent) {
      onDeleteEvent(eventToDelete);
      setToast({
        message: `"${eventToDelete.eventName}" has been deleted successfully`,
        type: 'success'
      });
      setEventToDelete(null);
    }
  }, [eventToDelete, onDeleteEvent]);

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  // Memoize the scroll handler to prevent recreation
  const handleScrollToEvents = useCallback(() => {
    console.log('CalendarGrid: View All Events clicked');

    if (typeof onViewAllEvents === 'function') {
      try { 
        onViewAllEvents(); 
      } catch (err) { 
        console.warn('onViewAllEvents threw', err); 
      }
    }

    const getScrollableParent = (node) => {
      if (!node) return null;
      let parent = node.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const isScrollable = (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
          && parent.scrollHeight > parent.clientHeight;
        if (isScrollable) return parent;
        parent = parent.parentElement;
      }
      return null;
    };

    setTimeout(() => {
      const target = (eventListRef && eventListRef.current)
        || document.getElementById('events-section')
        || document.getElementById('events-table')
        || document.querySelector('[data-events-section]');

      if (target) {
        const scrollContainer = getScrollableParent(target);
        if (scrollContainer) {
          const top = target.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top + scrollContainer.scrollTop;
          scrollContainer.scrollTo({ top, behavior: 'smooth' });
        } else {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 150);
  }, [onViewAllEvents, eventListRef]);

  // Memoize download handler
  const handleDownload = useCallback(async (format) => {
    setShowDownloadMenu(false);
    setDownloading(true);
    
    try {
      if (!calendarConfig || !calendarConfig.academicYear) {
        throw new Error('Academic year information is not available. Please refresh the page and try again.');
      }

      const academicYearId = calendarConfig.academicYear._id || calendarConfig.academicYear.id;
      const semesterType = selectedSemesterType;
      const token = localStorage.getItem('token');

      if (!academicYearId) throw new Error('Academic year ID is missing');
      if (!tenantId) throw new Error('Tenant ID is missing');
      if (!token) throw new Error('Authentication token is missing');

      const params = { tenantId, format };
      if (semesterType && calendarConfig.academicYear.isSemesterBased) {
        params.semesterType = semesterType;
      }

      const response = await api.get(
        `/api/academic-year/calendar/download/${academicYearId}`,
        {
          params,
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.data || response.data.size === 0) {
        throw new Error('Server returned empty file');
      }

      const blob = new Blob([response.data], {
        type: format === 'pdf' 
          ? 'application/pdf' 
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const academicYearName = calendarConfig.academicYear.name.replace(/[^a-zA-Z0-9]/g, '_');
      const semesterSuffix = semesterType ? `_${semesterType}` : '';
      const fileExtension = format === 'pdf' ? 'pdf' : 'xlsx';
      const filename = `academic_calendar_${academicYearName}${semesterSuffix}.${fileExtension}`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setToast({
        message: `Calendar downloaded successfully as ${format.toUpperCase()}`,
        type: 'success'
      });
      
    } catch (err) {
      let errorMessage = 'Download failed: ';
      
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage += 'Download endpoint not found';
        } else if (err.response.status === 401) {
          errorMessage += 'Authentication failed';
        } else if (err.response.status === 403) {
          errorMessage += 'Access denied';
        } else if (err.response.status === 500) {
          errorMessage += 'Server error';
        } else if (err.response.data?.message) {
          errorMessage += err.response.data.message;
        } else {
          errorMessage += `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        errorMessage += 'No response from server';
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      setToast({
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setDownloading(false);
    }
  }, [calendarConfig, selectedSemesterType, tenantId]);

  // Memoize day names
  const dayNames = useMemo(() => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], []);

  // Memoize calendar dates calculation
  const calendarDates = useMemo(() => {
    if (!calendarConfig || !calendarConfig.academicYear) return [];
    
    let start, end;
    if (calendarConfig.academicYear.isSemesterBased) {
      const semester = selectedSemesterType === 'odd'
        ? calendarConfig.semesterOptions?.find((s) => s.type === 'odd')
        : calendarConfig.semesterOptions?.find((s) => s.type === 'even');
      start = semester?.startDate ? moment(semester.startDate) : null;
      end = semester?.endDate ? moment(semester.endDate) : null;
    } else {
      start = calendarConfig.academicYear.startDate ? moment(calendarConfig.academicYear.startDate) : null;
      end = calendarConfig.academicYear.endDate ? moment(calendarConfig.academicYear.endDate) : null;
    }
    
    if (!start?.isValid() || !end?.isValid()) return [];
    
    const dates = [];
    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      dates.push(current.clone());
      current.add(1, 'day');
    }
    return dates;
  }, [calendarConfig, selectedSemesterType]);

  // Memoize unblocked Sundays set for faster lookup
  const unblockedSundaysSet = useMemo(() => {
    return new Set(unblockedSundays || []);
  }, [unblockedSundays]);

  // Memoize events map for faster lookup
  const eventsMap = useMemo(() => {
    const map = new Map();
    if (calendarEvents && calendarEvents.length > 0) {
      calendarEvents.forEach(event => {
        const normalizedEvent = {
          ...event,
          id: event.id || event._id,
        };
        if (normalizedEvent.startDate) {
          const dateStr = moment(normalizedEvent.startDate).format('YYYY-MM-DD');
          if (!map.has(dateStr)) {
            map.set(dateStr, []);
          }
          map.get(dateStr).push(normalizedEvent);
        }
      });
    }
    return map;
  }, [calendarEvents]);

  // Optimized function to check if Sunday is unblocked
  const isUnblockedSunday = useCallback((date) => {
    const dateStr = date.format('YYYY-MM-DD');
    return unblockedSundaysSet.has(dateStr);
  }, [unblockedSundaysSet]);

  // Optimized function to get events for a date
  const getEventsForDate = useCallback((dateStr) => {
    return eventsMap.get(dateStr) || [];
  }, [eventsMap]);

  // Group dates by month
  const monthsGrouped = useMemo(() => {
    const monthsMap = {};
    calendarDates.forEach((date) => {
      const key = date.format('YYYY-MM');
      if (!monthsMap[key]) monthsMap[key] = [];
      monthsMap[key].push(date);
    });
    return monthsMap;
  }, [calendarDates]);

  // Early return if no calendar config
  if (!calendarConfig || !calendarConfig.academicYear) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading calendar configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Toast Notifications */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={handleToastClose} 
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setEventToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        eventName={eventToDelete?.eventName}
      />

      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        {/* Header Section - Enhanced Design */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 pb-6 border-b border-gray-100">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {calendarConfig.academicYear.name}
              </span>
              <span className="text-sm font-normal text-gray-400">Calendar</span>
            </h1>
            <p className="text-gray-500 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500"></span>
              {calendarConfig.academicYear.isSemesterBased 
                ? `${selectedSemesterType ? selectedSemesterType.charAt(0).toUpperCase() + selectedSemesterType.slice(1) : ''} Semester View`
                : 'Annual View'
              }
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
              onClick={handleScrollToEvents}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              View All Events
            </button>

            <div className="relative">
              <button
                onClick={() => setShowDownloadMenu((v) => !v)}
                disabled={downloading}
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                {downloading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Downloading...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 10l5 5 5-5M12 15V3m9 9H3" />
                    </svg>
                    Download
                  </>
                )}
              </button>
              
              {showDownloadMenu && (
                <>
                  <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-2xl shadow-xl z-10 overflow-hidden">
                    <button
                      onClick={() => handleDownload('pdf')}
                      className="flex items-center gap-4 w-full text-left px-5 py-4 hover:bg-blue-50 border-b border-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">PDF Document</div>
                        <div className="text-sm text-gray-500">Best for printing</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleDownload('excel')}
                      className="flex items-center gap-4 w-full text-left px-5 py-4 hover:bg-green-50 transition-colors"
                    >
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Excel Spreadsheet</div>
                        <div className="text-sm text-gray-500">Best for data analysis</div>
                      </div>
                    </button>
                  </div>
                  <div 
                    className="fixed inset-0 z-0" 
                    onClick={() => setShowDownloadMenu(false)}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Legend & Quick Actions - Enhanced */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border border-gray-100">
          <div className="flex flex-wrap gap-4 mb-3 lg:mb-0">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 px-3 py-1.5 bg-white rounded-lg shadow-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
              Academic
            </span>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 px-3 py-1.5 bg-white rounded-lg shadow-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
              Exam
            </span>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 px-3 py-1.5 bg-white rounded-lg shadow-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
              Holiday
            </span>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 px-3 py-1.5 bg-white rounded-lg shadow-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
              Administrative
            </span>
          </div>
          
          <div className="text-sm text-gray-600 bg-white px-4 py-2 rounded-xl border border-gray-200 shadow-sm flex items-center gap-2">
            <span className="text-lg">💡</span>
            <span>Click dates to add events</span>
            <span className="text-gray-300">|</span>
            <span className="text-xs text-gray-400">Hover events to delete</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="space-y-8">
          {Object.entries(monthsGrouped).map(([monthKey, monthDates]) => {
            const firstDate = monthDates[0];
            const lastDate = monthDates[monthDates.length - 1];
            const firstDayOfWeek = firstDate.day();
            const lastDayOfWeek = lastDate.day();
            
            const leadingEmptyCells = Array(firstDayOfWeek).fill(null);
            const trailingEmptyCells = Array(6 - lastDayOfWeek).fill(null);

            return (
              <div key={monthKey} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                {/* Month Header - Enhanced */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">
                    {firstDate.format('MMMM YYYY')}
                  </h3>
                  <span className="text-blue-100 text-sm bg-white/20 px-3 py-1 rounded-full">
                    {monthDates.length} days
                  </span>
                </div>
                
                {/* Day Headers */}
                <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                  {dayNames.map((day) => (
                    <div
                      key={day}
                      className={`text-center font-semibold py-3 text-sm ${
                        day === 'Sun' ? 'text-red-600 bg-red-50/50' : 'text-gray-600'
                      }`}
                    >
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-px bg-gray-200">
                  {leadingEmptyCells.map((_, idx) => (
                    <div key={`empty-start-${monthKey}-${idx}`} className="bg-gray-50 min-h-[130px]" />
                  ))}
                  
                  {monthDates.map((date) => {
                    const dateStr = date.format('YYYY-MM-DD');
                    const isSunday = date.day() === 0;
                    const isUnblocked = isUnblockedSunday(date);
                    const events = getEventsForDate(dateStr);
                    const isToday = date.isSame(moment(), 'day');

                    return (
                      <DayCell
                        key={dateStr}
                        date={date}
                        isSunday={isSunday}
                        isUnblocked={isUnblocked}
                        events={events}
                        isToday={isToday}
                        onDateClick={onDateClick}
                        onEventClick={onEventClick}
                        onDeleteEvent={handleDeleteClick}
                        onUnblockSunday={onUnblockSunday}
                        onBlockSunday={onBlockSunday}
                      />
                    );
                  })}
                  
                  {trailingEmptyCells.map((_, idx) => (
                    <div key={`empty-end-${monthKey}-${idx}`} className="bg-gray-50 min-h-[130px]" />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Hint - Enhanced */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-6 py-3.5 rounded-2xl border border-blue-100 shadow-sm">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium">
              Click on any date to add events. Click <span className="text-green-600 font-semibold">"Unblock"</span> on Sundays to allow event scheduling. 
              <span className="text-red-500 font-semibold ml-1">Hover over events to delete</span>
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(CalendarGrid);